import { InteractiveProjectHandler } from '../interactiveProjectHandler';
import { _ASSET_TYPE } from '@rhinov/asset-manager-service';
import { ArrangementGroup, ArrangementObject, Commands, Entity, Events, eventsManager, math, RenderCamera } from '@rhinov/savane-js';
import { WebglFurniture } from '../../webgl/furniture';
import { WebglEntity } from '../../webgl/entity';


export class ReplaceArrangementCommand extends Commands.Command {
    
    private _handler: any;
    private _toReplace: ArrangementObject;
    private _replaceBy: ArrangementObject;
    private _populatedReplaceBy: ArrangementGroup | null;
    private _hiddenInCameras: Map<RenderCamera, number[]> = new Map();
    private _newHiddenInCameras: Map<RenderCamera, number[]> = new Map();
    private _isSubCommand: any;
    private _commands: any[];

    constructor(handler: InteractiveProjectHandler, toReplace: ArrangementObject, replaceBy: ArrangementObject, isSubCommand: boolean) {
        super();

        replaceBy.isAnchorActive = false;
        toReplace.isAnchorActive = false;
        
        this._handler = handler;
        this._toReplace = toReplace;
        this._replaceBy = replaceBy;
        this._populatedReplaceBy = null;

        // If _toReplace is the master of a group, collect per-camera which child IDs are hidden
        // (root ID is preserved on replace, but populated children get new IDs)
        if (this._toReplace.parent instanceof ArrangementGroup &&
            this._toReplace.parent.masterObjectId === this._toReplace.objectId) {
            let cameras = this._toReplace.scene!.renderCameras;
            let childObjects = this._toReplace.parent.arrangementObjectsRec;
            cameras.forEach(camera => {
                let hiddenIds = childObjects
                    .filter(item => camera.excludedObjectIds.indexOf(item.id) !== -1)
                    .map(item => item.id);
                if (hiddenIds.length > 0) {
                    this._hiddenInCameras.set(camera, hiddenIds);
                }
            });
        }

        this._replaceBy.id = this._toReplace.id;

        this._isSubCommand = isSubCommand;

        if (!this._isSubCommand) {
            if (toReplace.initialObjectId) {
                replaceBy.initialObjectId = toReplace.initialObjectId;
            } else {
                replaceBy.initialObjectId = toReplace.objectId;
            }
        }

        this._commands = [];
    }

    // Command name returned from a global enum
    name = () : string => {
        return "ReplaceArrangementCommand";
    }

    assetType = () => {
        return _ASSET_TYPE.ARRANGEMENTS;
    }

    undo = () : void => {
        let toReplace: Entity = this._toReplace;
        let parent = this._toReplace.parent;
        if (parent instanceof ArrangementGroup) {
            if (parent.masterObjectId === this._toReplace.objectId) {
                toReplace = this._toReplace.parent!;
                parent = parent.parent;
            }
        }

        var position = this._populatedReplaceBy ? this._populatedReplaceBy.position : this._replaceBy.position;
        if (toReplace instanceof ArrangementObject) {
            if (toReplace.anchor && toReplace.anchor[1] === -3 || this._replaceBy.anchor && this._replaceBy.anchor[1] === -3) {
                position[2] += (this._replaceBy.height - toReplace.height) / 2;
            } else {
                position[2] -= (this._replaceBy.height - toReplace.height) / 2;
            }
        }

        let lengthRatio = 1;
        let widthRatio = 1;
        let heightRatio = 1;
        if (toReplace instanceof ArrangementObject) {
            if (toReplace.originalLength) {
                lengthRatio = toReplace.length / toReplace.originalLength;
            }
            if (toReplace.originalWidth) {
                widthRatio = toReplace.width / toReplace.originalWidth;
            }
            if (toReplace.originalHeight) {
                heightRatio = toReplace.height / toReplace.originalHeight;
            }
        }
        toReplace.transform.localScale = math.vec3.fromValues(lengthRatio, widthRatio, heightRatio);

        let toRemove = this._populatedReplaceBy ? this._populatedReplaceBy : this._replaceBy;
        let toAdd: Entity = this._toReplace;
        let forceLoadedCallback = true;
        if (this._toReplace.parent instanceof ArrangementGroup) {
            if (this._toReplace.parent.masterObjectId) {
                toAdd = this._toReplace.parent;
                forceLoadedCallback = false;
            }
        }
        toAdd.position = position;
        if (this._populatedReplaceBy) {
            toAdd.floorHeight = this._populatedReplaceBy.floorHeight;
        }

        this._handler.scene.removeEntityTree(toRemove);
        this._handler.scene.addEntityTree(toAdd, (glEntity: any) => {
            if (!this._isSubCommand) {
                this._handler.scene.detachSelection();
                this._handler.scene.toggleSelection(this._handler.scene.selection(), false);
                this._handler.scene.selection().splice(0, this._handler.scene.selection().length);
            }
            
            for (var i = this._commands.length - 1; i >= 0; --i) {
                this._commands[i].undo();
            }

            // Restore camera exclusions: remove new child IDs, hide all objects in the restored group
            this._newHiddenInCameras.forEach((newIds, camera) => {
                newIds.forEach(id => {
                    let idx = camera.excludedObjectIds.indexOf(id);
                    if (idx !== -1) camera.excludedObjectIds.splice(idx, 1);
                });
                if (this._hiddenInCameras.has(camera)) {
                    let restoredIds = (this._toReplace.parent as ArrangementGroup).arrangementObjectsRec.map(item => item.id);
                    restoredIds.forEach(id => {
                        if (camera.excludedObjectIds.indexOf(id) === -1) {
                            camera.excludedObjectIds.push(id);
                        }
                    });
                }
            });

            if (this._isSubCommand) return;
            this._handler.scene.selection().push(glEntity.entity.parent instanceof ArrangementGroup &&
                glEntity.entity.parent.masterObjectId === glEntity.entity.objectId ? glEntity.entity.parent : glEntity.entity);
            this._handler.scene.toggleSelection(this._handler.scene.selection(), true);
            eventsManager.instance.dispatch(Events.SELECTED_ARRANGEMENT, { entity: glEntity.entity, point: null });
            eventsManager.instance.dispatch(Events.RHINOV_FORMAT_UPDATED, { scene: glEntity.entity.scene, command: null });
            this._handler.UpdateCamera();
            this._handler.scene.render();
        }, forceLoadedCallback);
    }

    redo = () : void => {
        this._exec(true, null as any);
    }

    execute = () : void => {
        this._exec(false, null as any);
    }

    _exec = (redo: boolean, callback: CallableFunction | null) : void => {
        this._commands = [];
        var position = this._toReplace.position;
        math.mat4.copy(this._replaceBy.transform.localMatrix, this._toReplace.transform.globalMatrix);
        
        var differencial = 0;
        if (this._toReplace.anchor && this._toReplace.anchor[1] === -3 || this._replaceBy.anchor && this._replaceBy.anchor[1] === -3) {
            differencial = (this._toReplace.height - this._replaceBy.height) / 2;
            position[2] += differencial;
        } else {
            differencial = -(this._toReplace.height - this._replaceBy.height) / 2;
            position[2] += differencial;
        }
        this._replaceBy.position = position;
        this._replaceBy.transform.localScale = math.vec3.fromValues(1, 1, 1);

        let parent = this._toReplace.parent;
        let toReplace: Entity = this._toReplace;
        let forceLoadedCallback = true;
        if (parent instanceof ArrangementGroup) {
            if (parent.masterObjectId === this._toReplace.objectId) {
                toReplace = this._toReplace.parent!;
                parent = parent.parent;
                forceLoadedCallback = false;
            }
        }

        this._handler.scene.removeEntityTree(toReplace);

        if (!this._isSubCommand) {
            this._handler.scene.detachSelection();
            this._handler.scene.toggleSelection(this._handler.scene.selection(), false);
            this._handler.scene.selection().splice(0, this._handler.scene.selection().length);
        }

        this._commands.push(new Commands.ReplaceEntitiesCommand([toReplace], [{entity: this._replaceBy, parent: parent}]));
        for (var i = 0; i < this._commands.length; ++i) {
            this._commands[i].execute();
        }
    
        this._handler.scene.removeEntityTree(toReplace);
        this._handler.scene.addEntityTree(this._replaceBy, (glEntity: WebglFurniture) => {
            if (!this._isSubCommand) {
                this._handler.scene.selection().push(glEntity.entity);
                eventsManager.instance.dispatch(Events.NEED_POPULATE, {callback: (command: Commands.ReplaceEntitiesCommand, empty: boolean) => {
                    if (!empty) {
                        this._commands.push(command);
                        command.execute();
                        this._populatedReplaceBy = command.execDatas().replacingEntitiesAndParents[0].entity;
                        
                        this._handler.scene.removeEntityTree(glEntity.entity);
                        this._handler.scene.addEntityTree(this._populatedReplaceBy, (glEntity: WebglEntity) => {
                            this._handler.scene.selection().splice(0, this._handler.scene.selection().length);
                            this._handler.scene.selection().push(glEntity.entity.parent);
                            this._handler.scene.toggleSelection(this._handler.scene.selection(), true);

                            // Transfer camera exclusions from old children to new children
                            if (this._hiddenInCameras.size > 0) {
                                let newChildIds = this._populatedReplaceBy!.arrangementObjectsRec
                                    .map(item => item.id);
                                this._newHiddenInCameras.clear();
                                this._hiddenInCameras.forEach((oldIds, camera) => {
                                    oldIds.forEach(id => {
                                        let idx = camera.excludedObjectIds.indexOf(id);
                                        if (idx !== -1) camera.excludedObjectIds.splice(idx, 1);
                                    });
                                    newChildIds.forEach(id => {
                                        if (camera.excludedObjectIds.indexOf(id) === -1) {
                                            camera.excludedObjectIds.push(id);
                                        }
                                    });
                                    this._newHiddenInCameras.set(camera, newChildIds.slice());
                                });
                            }

                            eventsManager.instance.dispatch(Events.SELECTED_ARRANGEMENT, { entity: glEntity.entity, point: null });
                            eventsManager.instance.dispatch(Events.RHINOV_FORMAT_UPDATED, { scene: glEntity.entity.scene, command: redo ? null : this });
                        });
                        
                    } else {
                        this._handler.scene.addEntityTree(this._replaceBy, (glEntity: WebglEntity) => {
                            this._handler.scene.selection().splice(0, this._handler.scene.selection().length);
                            this._handler.scene.selection().push(glEntity.entity);
                            this._handler.scene.toggleSelection(this._handler.scene.selection(), true);
                            eventsManager.instance.dispatch(Events.SELECTED_ARRANGEMENT, { entity: glEntity.entity, point: null });
                            eventsManager.instance.dispatch(Events.RHINOV_FORMAT_UPDATED, { scene: glEntity.entity.scene, command: redo ? null : this });
                        }, true);
                    }

                    this._handler.UpdateCamera();
                    this._handler.scene.render();
                }});
            }
        }, forceLoadedCallback);

        if (callback) {
            callback();
        }
    }

}
